import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  Timestamp,
  where,
  query,
  getDocs,
  limit
} from 'firebase/firestore';
import { db, auth } from './config';
import { cache } from '../utils/cacheManager';

const DASHBOARD_CACHE_COLLECTION = 'dashboardCache';

interface DashboardCache {
  id?: string;
  type: 'library' | 'news' | 'programs' | 'accounting';
  data: any;
  lastUpdated: any;
  ttl: number; // Time to live in seconds
  version: number;
}

// Get cached dashboard data
export const getCachedDashboardData = async <T>(
  dashboardType: string,
  forceRefresh: boolean = false,
  ttl: number = 15 * 60 // 15 minutes default
): Promise<{ success: boolean; data?: T; fromCache: boolean; error?: string }> => {
  try {
    const cacheKey = `dashboard_${dashboardType}`;
    
    // First check memory cache
    const memoryCache = cache.getCacheState(cacheKey);
    if (!forceRefresh && memoryCache.hasCache && memoryCache.isFresh) {
      const data = await cache.get(cacheKey, async () => {
        // This won't be called if cache is fresh
        return { success: true, data: null };
      }, ttl * 1000);

      // data may be null in some edge cases; coerce safely
      return {
        success: true,
        data: (data && (data.data as T)) || (null as any),
        fromCache: true
      };
    }
    
    // Check Firestore cache
    const cacheRef = doc(db, DASHBOARD_CACHE_COLLECTION, dashboardType);
    const cacheSnap = await getDoc(cacheRef);
    
    if (!forceRefresh && cacheSnap.exists()) {
      const cacheData = cacheSnap.data() as DashboardCache;
      const cacheAge = Date.now() - cacheData.lastUpdated.toMillis();
      
      // If cache is still valid
      if (cacheAge < cacheData.ttl * 1000) {
        // Store in memory cache
        await cache.get(cacheKey, async () => ({
          success: true,
          data: cacheData.data
        }), ttl * 1000);
        
        return { 
          success: true, 
          data: cacheData.data, 
          fromCache: true 
        };
      }

      // Avoid expensive client-side aggregation when the dashboard cache is stale.
      // A stale summary is much cheaper than every admin browser reading hundreds
      // or thousands of source documents. Fresh recomputes should run server-side.
      await cache.get(cacheKey, async () => ({
        success: true,
        data: cacheData.data
      }), 5 * 60 * 1000);

      return {
        success: true,
        data: cacheData.data,
        fromCache: true
      };
    }
    
    // Cache is stale or doesn't exist. Prefer server-side recompute via HTTP endpoint
    // to avoid clients performing heavy aggregation. If no endpoint is configured
    // or if the endpoint call fails, fall back to local compute.
    let freshData: any = null;
    const recomputeEndpoint = process.env.REACT_APP_RECOMPUTE_DASHBOARD_URL;
    const recomputeSecret = process.env.REACT_APP_RECOMPUTE_DASHBOARD_SECRET;

    if (recomputeEndpoint) {
      try {
        const headers: Record<string,string> = { 'Content-Type': 'application/json' };
        if (recomputeSecret) headers['x-recompute-secret'] = recomputeSecret;

        // Timeout wrapper
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);

        const resp = await fetch(recomputeEndpoint, { method: 'POST', headers, signal: controller.signal });
        clearTimeout(timeout);

        if (resp.ok) {
          // Re-read the Firestore cache document that the function should have written
          const cacheRef = doc(db, DASHBOARD_CACHE_COLLECTION, dashboardType);
          const updatedSnap = await getDoc(cacheRef);
          if (updatedSnap.exists()) {
            const updated = updatedSnap.data() as DashboardCache;
            freshData = updated.data;
          }
        } else {
          console.warn('Recompute endpoint returned non-OK', resp.status);
        }
      } catch (e) {
        console.warn('Recompute endpoint call failed, falling back to local compute', e);
      }
    }

    // If no freshData yet, fall back to local compute (preserve existing behavior)
    if (!freshData) {
      switch (dashboardType) {
        case 'library':
          freshData = await computeLibraryDashboardData();
          break;
        case 'news':
          freshData = await computeNewsDashboardData();
          break;
        case 'programs':
          // computeProgramsDashboardData not implemented yet; fall back to minimal
          freshData = { stats: {}, monthlyData: [], ageDistribution: [], topBooks: [], topAttendees: [] };
          break;
        default:
          throw new Error(`Unknown dashboard type: ${dashboardType}`);
      }
    }
    
    // Update Firestore cache
    const cacheUpdate: DashboardCache = {
      type: dashboardType as any,
      data: freshData,
      lastUpdated: serverTimestamp(),
      ttl: ttl,
      version: 1
    };
    
    await setDoc(cacheRef, cacheUpdate, { merge: true });
    
    // Update memory cache
    cache.get(cacheKey, async () => ({
      success: true,
      data: freshData
    }), ttl * 1000);
    
    return { 
      success: true, 
      data: freshData, 
      fromCache: false 
    };
    
  } catch (error: any) {
    console.error('Error getting cached dashboard data:', error);
    return { 
      success: false, 
      fromCache: false, 
      error: error.message 
    };
  }
};

// Compute library dashboard data (expensive operation - runs infrequently)
const computeLibraryDashboardData = async (): Promise<any> => {
  console.log('🔄 Computing fresh library dashboard data...');
  
  // Get stats from precomputed document
  const statsRef = doc(db, 'libraryMeta', 'stats');
  const statsSnap = await getDoc(statsRef);
  const stats = statsSnap.exists() ? statsSnap.data() : {};
  
  // Get recent attendance (limited to 1000 records for efficiency)
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  
  const attendanceQuery = query(
    collection(db, 'libraryAttendance'),
    where('timeRecorded', '>=', Timestamp.fromDate(oneMonthAgo)),
    limit(1000)
  );
  
  const attendanceSnapshot = await getDocs(attendanceQuery);
  const attendanceData = attendanceSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
  
  // Compute monthly data
  const monthlyData = computeMonthlyAttendance(attendanceData);
  
  // Get recent members (limited)
  const membersQuery = query(
    collection(db, 'libraryMembers'),
    limit(200)
  );
  
  const membersSnapshot = await getDocs(membersQuery);
  const ageDistribution = computeAgeDistribution(membersSnapshot.docs);
  
  // Compute top books (from attendance)
  const topBooks = computeTopBooks(attendanceData);
  
  // Compute top attendees
  const topAttendees = computeTopAttendees(attendanceData);
  
  return {
    stats: {
      totalMembers: stats.totalMembers || 0,
      activeMembers: stats.activeMembers || 0,
      maleMembers: stats.maleMembers || 0,
      femaleMembers: stats.femaleMembers || 0,
      totalBooks: stats.totalBooks || 0,
      availableBooks: stats.availableBooks || 0,
      totalAttendance: stats.totalAttendance || 0,
      todayAttendance: stats.todayAttendance || 0
    },
    monthlyData,
    ageDistribution,
    topBooks,
    topAttendees,
    computedAt: new Date().toISOString()
  };
};

// Helper functions for computing dashboard data
const computeMonthlyAttendance = (attendanceData: any[]): any[] => {
  const monthlyMap: Record<string, { attendance: number; uniqueMembers: Set<string> }> = {};
  
  attendanceData.forEach(record => {
    const date = new Date(record.timeRecorded?.toDate?.() || record.timeRecorded);
    const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    
    if (!monthlyMap[monthYear]) {
      monthlyMap[monthYear] = {
        attendance: 0,
        uniqueMembers: new Set()
      };
    }
    
    monthlyMap[monthYear].attendance++;
    monthlyMap[monthYear].uniqueMembers.add(record.memberId);
  });
  
  // Convert to array for chart, get last 6 months
  const months = Object.keys(monthlyMap).sort().slice(-6);
  return months.map(monthKey => {
    const [year, month] = monthKey.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month) - 1];
    
    return {
      month: `${monthName} ${year.slice(-2)}`,
      attendance: monthlyMap[monthKey].attendance,
      uniqueMembers: monthlyMap[monthKey].uniqueMembers.size
    };
  });
};

const computeAgeDistribution = (memberDocs: any[]): any[] => {
  const ageGroups = {
    children: 0,
    teenagers: 0,
    youngAdults: 0,
    senior: 0
  };
  
  memberDocs.forEach(doc => {
    const data = doc.data();
    const dob = data.dob;
    if (!dob) return;
    
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age <= 12) ageGroups.children++;
    else if (age <= 17) ageGroups.teenagers++;
    else if (age <= 24) ageGroups.youngAdults++;
    else ageGroups.senior++;
  });
  
  return [
    { name: 'Children', value: ageGroups.children, color: '#FF6384' },
    { name: 'Teenagers', value: ageGroups.teenagers, color: '#36A2EB' },
    { name: 'Young Adults', value: ageGroups.youngAdults, color: '#FFCE56' },
    { name: 'Senior', value: ageGroups.senior, color: '#4BC0C0' }
  ];
};

const computeTopBooks = (attendanceData: any[]): any[] => {
  const bookCounts: Record<string, number> = {};
  
  attendanceData.forEach(record => {
    const title = record.bookTitle?.trim();
    if (!title || title.toLowerCase().includes('self study')) return;
    
    bookCounts[title] = (bookCounts[title] || 0) + 1;
  });
  
  return Object.entries(bookCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([title, reads], index) => ({
      id: index + 1,
      title,
      reads,
      author: 'Unknown' // Would need book data to get author
    }));
};

const computeTopAttendees = (attendanceData: any[]): any[] => {
  const attendeeCounts: Record<string, { count: number; name: string; memberId: string }> = {};
  
  attendanceData.forEach(record => {
    const key = record.memberId;
    if (!attendeeCounts[key]) {
      attendeeCounts[key] = {
        count: 0,
        name: record.fullName || 'Unknown',
        memberId: key
      };
    }
    attendeeCounts[key].count++;
  });
  
  return Object.values(attendeeCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map((attendee, index) => ({
      id: index + 1,
      name: attendee.name,
      visits: attendee.count,
      memberId: attendee.memberId
    }));
};

// Update dashboard cache when data changes
export const invalidateDashboardCache = async (dashboardType: string): Promise<void> => {
  try {
    // Invalidate memory cache
    cache.invalidatePattern(new RegExp(`dashboard_${dashboardType}.*`));
    
    // Optionally: Trigger recomputation in background by calling a Cloud Function
    // Provide REACT_APP_RECOMPUTE_DASHBOARD_URL and optional REACT_APP_RECOMPUTE_DASHBOARD_SECRET in your environment
    try {
      const endpoint = process.env.REACT_APP_RECOMPUTE_DASHBOARD_URL;
      const secret = process.env.REACT_APP_RECOMPUTE_DASHBOARD_SECRET;
      if (endpoint) {
        const headers: Record<string,string> = {};
        if (secret) headers['x-recompute-secret'] = secret;
        // Fire-and-forget POST but log response when available
        fetch(endpoint, { method: 'POST', headers })
          .then(async (resp) => {
            if (!resp.ok) {
              let text = '';
              try { text = await resp.text(); } catch (e) { /* ignore */ }
              console.warn('Recompute endpoint returned non-OK', resp.status, text);
            } else {
              console.log('Recompute endpoint triggered successfully', resp.status);
            }
          })
          .catch(err => console.warn('Failed to call recompute endpoint', err));
      }
    } catch (e) {
      console.warn('Error attempting to call recompute endpoint', e);
    }

    console.log(`Dashboard cache invalidated for: ${dashboardType}`);
  } catch (error) {
    console.error('Error invalidating dashboard cache:', error);
  }
};

// Initialize dashboard cache system
export const initializeDashboardCache = async (): Promise<void> => {
  try {
    // Avoid initializing cache when unauthenticated (prevents permission errors)
    if (!auth || !auth.currentUser) {
      console.log('Skipping initializeDashboardCache: no authenticated user');
      return;
    }

    // Create cache documents if they don't exist
    const dashboardTypes = ['library', 'news', 'programs', 'accounting'];
    
    for (const type of dashboardTypes) {
      const cacheRef = doc(db, DASHBOARD_CACHE_COLLECTION, type);
      const cacheSnap = await getDoc(cacheRef);
      
      if (!cacheSnap.exists()) {
        await setDoc(cacheRef, {
          type,
          data: {},
          lastUpdated: serverTimestamp(),
          ttl: 900, // 15 minutes
          version: 1
        });
      }
    }
    
    console.log('✅ Dashboard cache system initialized');
  } catch (error) {
    console.error('Error initializing dashboard cache:', error);
  }
};

function computeNewsDashboardData(): any {
  throw new Error('Function not implemented.');
}
